import { useMemo } from "react";
import { motion } from "framer-motion";
import { BookOpen, FileText, GraduationCap, Globe } from "lucide-react";
import { TILE_ICON_SIZE } from "../components/GreenIconTile";
import researchData from "../data/publicaciones.json";
import { useCmsDoc } from "../hooks/useCmsDoc";

const ScientificResearchPage = () => {
  const directorFallback = useMemo(
    () => researchData.investigacionCientifica.director,
    []
  );
  const pubsFallback = useMemo(
    () => ({ items: researchData.investigacionCientifica.publicaciones }),
    []
  );
  const { data: directorCms } = useCmsDoc("research", "director", directorFallback);
  const { data: pubsDoc } = useCmsDoc("research", "publications", pubsFallback);

  const director = directorCms || researchData.investigacionCientifica.director;
  const publicaciones =
    pubsDoc?.items || researchData.investigacionCientifica.publicaciones;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-24 pb-20 bg-surface min-h-screen font-body"
    >
      <div className="container mx-auto px-6">
        <header className="mb-16 text-center flex flex-col items-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-8 h-[2px] bg-brand-accent"></span>
            <span className="text-brand-primary font-bold uppercase tracking-[0.3em] text-xs">
              Ciencia y Evidencia
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black text-brand-dark tracking-tighter mb-6">
            Investigación <span className="text-brand-primary">Científica</span>
          </h1>
          <p className="text-lg text-brand-dark/70 max-w-3xl leading-relaxed mx-auto">
            Nuestro compromiso con la salud ocupacional se fundamenta en la investigación científica rigurosa,
            liderando estudios de impacto internacional sobre enfermedades ocupacionales y su prevención.
          </p>
        </header>

        <section className="mb-20 bg-white p-8 md:p-12 rounded-[40px] shadow-xl border border-brand-soft">
          <div className="grid md:grid-cols-12 gap-2 items-center">
            <div className="md:col-span-4 flex justify-center">
              <div className="w-68 h-88 rounded-2xl bg-brand-dark/10 flex items-center justify-center shadow-2xl overflow-hidden relative">
                <img
                  src="https://i.ibb.co/v4Bw47rV/drjarquin-1.webp"
                  alt={director.nombre}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="md:col-span-8 space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-brand-dark">{director.nombre}</h2>
                <p className="text-brand-primary font-bold uppercase tracking-widest text-sm mt-1">
                  {director.cargo}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {(director.titulos || []).map((titulo, index) => (
                  <span
                    key={index}
                    className="px-4 py-1 bg-brand-soft text-brand-primary rounded-full text-xs font-bold flex items-center gap-2 border border-brand-primary/10"
                  >
                    <GraduationCap size={14} /> {titulo}
                  </span>
                ))}
              </div>
              <div className="flex gap-4 items-start bg-brand-soft/30 p-6 rounded-2xl border border-brand-soft">
                <Globe className="text-brand-primary shrink-0" size={24} />
                <p className="text-brand-dark/80 italic leading-relaxed">
                  {director.afiliacion}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="flex items-center gap-4 mb-10">
            <BookOpen className="text-brand-primary" size={TILE_ICON_SIZE} />
            <h2 className="text-3xl font-bold text-brand-dark tracking-tight">
              Publicaciones Académicas
            </h2>
          </div>
          <div className="grid gap-6">
            {publicaciones.map((pub) => (
              <motion.div
                key={pub.id}
                whileHover={{ y: -5 }}
                className="bg-white p-8 rounded-[30px] shadow-md border border-brand-soft hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                  <span className="text-xs font-bold text-brand-primary uppercase tracking-widest bg-brand-soft px-3 py-1 rounded-lg w-fit">
                    {pub.revista} ({pub.anio})
                  </span>
                  {pub.doi && (
                    <span className="text-[10px] font-mono text-brand-dark/40">
                      DOI: {pub.doi}
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-bold text-brand-dark mb-4 leading-tight">
                  {pub.titulo}
                </h3>
                <p className="text-sm text-brand-dark/60 mb-6 italic leading-relaxed">
                  {pub.autores}
                </p>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-brand-soft">
                  <div className="text-xs text-brand-dark/40">
                    {pub.volumen && <span>Vol. {pub.volumen} </span>}
                    {pub.paginas && <span>Pág. {pub.paginas}</span>}
                  </div>
                  {pub.url && (
                    <a
                      href={pub.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-brand-primary font-bold text-xs hover:text-brand-dark transition-colors"
                    >
                      Ver Artículo <FileText size={16} />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </div>
    </motion.div>
  );
};

export default ScientificResearchPage;
